/**
 * @fileoverview `DsPreview` — cadre standardisé pour un exemple de composant.
 *
 * Utilisé par toutes les sections de la page `/design-system` pour présenter
 * un composant dans un fond neutre (grid checker léger) avec un padding
 * uniforme. Garantit une lecture cohérente d'une section à l'autre : on ne
 * confond pas ce qui est UI du composant avec les artefacts de mise en page.
 *
 * @example
 * ```tsx
 * <DsPreview label="primary">
 *   <Button>Payer</Button>
 * </DsPreview>
 * ```
 */

import type { ReactNode } from 'react';
import { cn } from '@gnpc/ui';

interface DsPreviewProps {
  /**
   * Étiquette optionnelle affichée en pied de cadre (nom de la variante,
   * du state ou du token illustré). Reste discrète, en petit gris.
   */
  label?: string;
  /**
   * Fond du cadre — par défaut un fond blanc sur un damier léger. `dark`
   * force un fond navy Quipux pour vérifier les composants sur fond sombre.
   */
  tone?: 'default' | 'dark';
  /**
   * Alignement du contenu dans le cadre. Par défaut `start` (top-left) pour
   * ne pas fausser la lecture des composants qui prennent 100% de largeur.
   */
  align?: 'start' | 'center';
  /** Classe supplémentaire pour cas particuliers (padding, height…). */
  className?: string;
  children: ReactNode;
}

/**
 * Cadre neutre pour un exemple de composant du design system.
 * Composant présentation only — aucune logique, aucun state.
 */
export function DsPreview({
  label,
  tone = 'default',
  align = 'start',
  className,
  children,
}: DsPreviewProps): JSX.Element {
  return (
    <figure className="space-y-2">
      <div
        className={cn(
          'flex min-h-24 rounded-lg border border-border p-6',
          align === 'center' ? 'items-center justify-center' : 'items-start',
          tone === 'dark'
            ? 'bg-quipux-navy-950 text-white'
            : 'bg-[repeating-linear-gradient(45deg,#fafafa,#fafafa_8px,#f4f4f5_8px,#f4f4f5_16px)]',
          className,
        )}
      >
        {children}
      </div>
      {label && (
        <figcaption className="font-mono text-xs text-muted-foreground">{label}</figcaption>
      )}
    </figure>
  );
}
